import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Badge from "../components/Badge";
import Banner from "../components/Banner";
import Card from "../components/Card";
import ChevronLeft from "../components/icons/ChevronLeft";
import GhostButton from "../components/GhostButton";
import LinkButton from "../components/LinkButton";
import { RowSpinner } from "../components/Loader/Loader";
import PrimaryButton from "../components/PrimaryButton";
import DecisionConfirm from "../components/syncunit/DecisionConfirm";
import FileRow from "../components/FileRow";
import { accountTitle, formatDate, formatPeriod, accountNumber, providerName } from "../components/syncunit/format";
import IndeterminateBar from "../components/syncunit/IndeterminateBar";
import OperationsTimeline from "../components/syncunit/OperationsTimeline";
import ReportTimeline from "../components/syncunit/ReportTimeline";
import Tabs from "../components/syncunit/Tabs";
import { MY_ACCOUNTS_PATH, SYNC_UNITS_PATH } from "../constants/syncRoutes";
import useNavigation, { useRouteParams } from "../hooks/use-navigation";
import useToken from "../hooks/use-token";
import {
  taxApi,
  useFinalizeInvestingAccountsSynchronizationMutation,
  useListInvestingAccountBrokerReportsQuery,
  useListInvestingAccountOperationsQuery,
  useReadInvestingAccountQuery,
  useRemoveFailedBrokerReportsMutation,
  useRollbackSynchronizationMutation,
} from "../store";
import { UNIFIED_UPLOAD_PATH } from "./UnifiedUploadPage";

export const SYNC_UNIT_POLLING_INTERVAL_MS = 3000;
// The first operations page OperationsTimeline reads — the same args, so RTK Query shares one request.
const FIRST_PAGE = { limit: 50 };
const STALE = new Set([404, 409]);
const isStale = (error) => STALE.has(error?.status);

function CenteredCard({ children }) {
  return (
    <Card data-testid="centered-card" className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-sm text-text-muted">
      {children}
    </Card>
  );
}

// SCR-03 error (failed) / error (failed, no report): the decision block in danger; no confirm and no add (AC-05c).
function FailedDecision({ syncUnit, title, busy, removing, rollbackOpener, onRemove, onRollback }) {
  const { t } = useTranslation();
  const report = syncUnit.failedBrokerReport;
  const removable = report && syncUnit.failedBrokerReportLinkType === "UPLOADED";

  return (
    <Card variant="danger" data-testid="decision-block" className="p-4 flex flex-col gap-3">
      <div>
        <p className="text-sm font-medium text-danger">
          {report ? t("syncUnit.failed.title", { fileName: report.fileName }) : t("syncUnit.failedNoReport.title")}
        </p>
        <p className="text-sm mt-1">
          {report ? t("syncUnit.failed.text", { account: title }) : t("syncUnit.failedNoReport.text", { account: title })}
        </p>
        {removable && <p className="text-xs text-text-muted mt-1">{t("syncUnit.failed.hint")}</p>}
        {!report && <p className="text-xs text-text-muted mt-1">{t("syncUnit.failedNoReport.hint")}</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        {removable && (
          <PrimaryButton size="lg" loading={removing} disabled={busy && !removing} onClick={onRemove}>
            {t("syncUnit.actions.removeReport")}
          </PrimaryButton>
        )}
        <GhostButton ref={rollbackOpener} size="lg" disabled={busy} onClick={onRollback} className="text-danger">
          {t("syncUnit.actions.rollbackAccount")}
        </GhostButton>
      </div>
    </Card>
  );
}

// «Звіти рахунку» of a FAILED SyncUnit: the launch reports, the broken one in its danger variant (AC-06).
function LaunchReports({ account, syncUnit }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium">{t("syncUnit.launchReports")}</p>
      <ul className="flex flex-col gap-2">
        {syncUnit.launchReports.map((report) => (
          <FileRow
            key={report.id}
            fileName={report.fileName}
            state={report.id === syncUnit.failedBrokerReport?.id ? "in-sync-failed" : "in-sync"}
            meta={t("syncUnit.reportMeta", {
              provider: providerName(account.dataProvider),
              account: accountNumber(account.originalId),
              period: formatPeriod(report.periodStart, report.periodEnd),
              date: formatDate(report.createDateTime),
            })}
          />
        ))}
      </ul>
    </div>
  );
}

// SCR-03 — SyncUnit рахунку, addressed by the account id (ADR-0002, ADR-0012). Every decision carries the version
// the investor saw (`openSyncUnit.id`); a 404 / 409 sends them back to SCR-02 in its stale state.
function SyncUnitPage({ pollingInterval = SYNC_UNIT_POLLING_INTERVAL_MS }) {
  const { t } = useTranslation();
  const token = useToken();
  const dispatch = useDispatch();
  const { navigate } = useNavigation();
  const { accountId } = useRouteParams();

  const [preparing, setPreparing] = useState(false);
  const { data, error, isLoading, refetch } = useReadInvestingAccountQuery(
    { accountId },
    { skip: !token || !accountId, refetchOnMountOrArgChange: true, pollingInterval: preparing ? pollingInterval : 0 },
  );
  const syncUnit = data?.openSyncUnit || null;
  const status = syncUnit?.status;
  useEffect(() => setPreparing(status === "SYNCING"), [status]);

  const title = data ? accountTitle(data.investingAccount) : "";
  // A stale version (404 / 409 on any read or action) hands the investor back to SCR-02 — once.
  const handedBack = useRef(false);
  const backToList = useCallback(
    (staleStatus) => {
      if (handedBack.current) return;
      handedBack.current = true;
      navigate(SYNC_UNITS_PATH, { state: { stale: { status: staleStatus, account: title } } });
    },
    [navigate, title],
  );

  // Where to go after a decision: the re-read list decides (screens.md §SCR-03 success states).
  const afterDecision = async (emptyTarget, toast) => {
    let rowsLeft = 0;
    // Subscribed for the read: the list keeps no unused data, an unsubscribed read would lose its result.
    const read = dispatch(taxApi.endpoints.readSynchronizingInvestingAccounts.initiate(undefined, { forceRefetch: true }));
    try {
      const list = await read.unwrap();
      rowsLeft = list?.synchronizingInvestingAccounts?.length || 0;
    } catch {
      rowsLeft = 1; // the list could not be read — SCR-02 shows its own error state
    } finally {
      read.unsubscribe();
    }
    navigate(rowsLeft > 0 ? SYNC_UNITS_PATH : emptyTarget, { state: { toast } });
  };

  const [finalize, { isLoading: confirming }] = useFinalizeInvestingAccountsSynchronizationMutation();
  const [rollback, { isLoading: rollingBack }] = useRollbackSynchronizationMutation();
  const [removeReports, { isLoading: removing }] = useRemoveFailedBrokerReportsMutation();
  const [actionError, setActionError] = useState(null);
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [rollbackError, setRollbackError] = useState(false);
  const [tab, setTab] = useState("operations");
  const rollbackOpener = useRef(null);

  const ready = status === "SYNCED";
  const versionArgs = { accountId, syncUnitId: syncUnit?.id };
  const { data: operations, error: operationsError } = useListInvestingAccountOperationsQuery(
    { ...versionArgs, ...FIRST_PAGE },
    { skip: !ready },
  );
  const { data: reports, error: reportsError } = useListInvestingAccountBrokerReportsQuery(versionArgs, { skip: !ready });
  const staleRead = [operationsError, reportsError].find(isStale);
  useEffect(() => {
    if (staleRead) backToList(staleRead.status);
  }, [staleRead, backToList]);

  const confirm = () => {
    setActionError(null);
    finalize({ investingAccounts: [{ id: accountId, syncUnitId: syncUnit.id }] })
      .unwrap()
      .then(() => afterDecision(MY_ACCOUNTS_PATH, t("syncUnit.toasts.confirmed", { account: title })))
      .catch((failure) => (isStale(failure) ? backToList(failure.status) : setActionError("confirm")));
  };

  const confirmRollback = () => {
    setRollbackError(false);
    rollback({ accountId, syncUnitId: syncUnit.id })
      .unwrap()
      .then(() => afterDecision(UNIFIED_UPLOAD_PATH, t("syncUnit.toasts.rolledBack", { account: title })))
      .catch((failure) => (isStale(failure) ? backToList(failure.status) : setRollbackError(true)));
  };

  // AC-06 / D18: only a new (UPLOADED) broken report can be removed; a new version without it prepares again, and
  // with no new reports left the server applies the consequences of a rollback (syncUnit: null).
  const removeReport = () => {
    setActionError(null);
    removeReports({ accountId, syncUnitId: syncUnit.id, brokerReportIds: [syncUnit.failedBrokerReport.id] })
      .unwrap()
      .then((result) =>
        navigate(SYNC_UNITS_PATH, {
          state: { toast: t(result?.syncUnit ? "syncUnit.toasts.reportRemoved" : "syncUnit.toasts.reportRemovedRolledBack") },
        }),
      )
      .catch((failure) => (isStale(failure) ? backToList(failure.status) : setActionError("remove")));
  };

  const addReport = () => navigate(UNIFIED_UPLOAD_PATH, { state: { addToAccount: { id: accountId, title } } });

  const back = (
    <LinkButton to={SYNC_UNITS_PATH}>
      <ChevronLeft />
      {t("syncUnit.back")}
    </LinkButton>
  );

  let body;
  if (isLoading || (!data && !error)) {
    body = (
      <CenteredCard>
        <RowSpinner />
        {t("syncUnit.loading")}
      </CenteredCard>
    );
  } else if (error && !isStale(error)) {
    body = (
      <Banner tone="danger">
        <p>{t("syncUnit.error")}</p>
        <LinkButton onClick={refetch}>{t("syncUnit.retry")}</LinkButton>
      </Banner>
    );
  } else if (!syncUnit) {
    body = <CenteredCard>{t("syncUnit.notFound")}</CenteredCard>;
  } else {
    const busy = confirming || rollingBack || removing;
    const failedState = status === "FAILED";
    const rollbackPanel = rollbackOpen && (
      <DecisionConfirm
        newCount={operations?.newCount ?? 0}
        reportCount={syncUnit.launchReports.length}
        failed={failedState}
        loading={rollingBack}
        error={rollbackError}
        returnFocusRef={rollbackOpener}
        onConfirm={confirmRollback}
        onClose={() => {
          setRollbackOpen(false);
          setRollbackError(false);
        }}
      />
    );
    body = (
      <>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight break-all min-w-0">{title}</h1>
          <Badge tone="accent">{providerName(data.investingAccount.dataProvider)}</Badge>
          <Badge tone="muted">
            {t(data.investingAccount.newAccount ? "syncCommon.badges.newAccount" : "syncCommon.badges.existingAccount")}
          </Badge>
        </div>

        {status === "SYNCING" && (
          <Card data-testid="preparing-card" className="p-4 flex flex-col gap-3">
            <p className="text-sm">{t("syncUnit.preparing")}</p>
            <IndeterminateBar label={t("syncUnit.preparing")} />
          </Card>
        )}

        {ready && (
          <>
            <Card data-testid="decision-block" className="p-4 flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-success-ink">{t("syncUnit.ready.title")}</p>
                <p className="text-xs text-text-muted mt-0.5">{t("syncUnit.ready.text")}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <GhostButton
                  ref={rollbackOpener}
                  size="lg"
                  disabled={busy}
                  onClick={() => setRollbackOpen(true)}
                  className="text-danger"
                >
                  {t("syncUnit.actions.rollback")}
                </GhostButton>
                <GhostButton size="lg" disabled={busy} onClick={addReport}>
                  {t("syncUnit.actions.addReport")}
                </GhostButton>
                <PrimaryButton size="xl" loading={confirming} disabled={rollingBack} onClick={confirm}>
                  {t("syncUnit.actions.confirm")}
                </PrimaryButton>
              </div>
            </Card>
            {rollbackPanel}
            {actionError === "confirm" && <Banner tone="danger">{t("syncUnit.errors.confirm")}</Banner>}
            <Tabs
              label={title}
              active={tab}
              onChange={setTab}
              tabs={[
                { id: "operations", label: t("syncUnit.tabs.operations", { count: operations?.totalCount ?? 0 }) },
                { id: "reports", label: t("syncUnit.tabs.reports", { count: reports?.reports?.length ?? 0 }) },
              ]}
            />
            {tab === "operations" ? (
              <OperationsTimeline accountId={accountId} syncUnitId={syncUnit.id} onStale={backToList} />
            ) : (
              <div className="flex flex-col gap-3">
                <p className="text-xs text-text-muted">{t("syncUnit.reportsHint")}</p>
                {reports && <ReportTimeline reports={reports.reports} gaps={reports.gaps} />}
              </div>
            )}
          </>
        )}
        {failedState && (
          <>
            <FailedDecision
              syncUnit={syncUnit}
              title={title}
              busy={busy}
              removing={removing}
              rollbackOpener={rollbackOpener}
              onRemove={removeReport}
              onRollback={() => setRollbackOpen(true)}
            />
            {rollbackPanel}
            {actionError === "remove" && <Banner tone="danger">{t("syncUnit.errors.remove")}</Banner>}
            <LaunchReports account={data.investingAccount} syncUnit={syncUnit} />
          </>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-5 md:px-10 py-8 max-w-[1040px]" data-testid="sync-unit-page" data-account-id={accountId}>
      {back}
      {body}
    </div>
  );
}

export default SyncUnitPage;
